import { ref, computed, onMounted } from 'vue';
import store from '@/store';
import router from '@/router';
import cloneDeep from 'lodash/cloneDeep';
import { config } from '@/config';
import { t } from '@/utils/i18n-helper';
import datosPersonalesService from '@/services/perfil/datosPersonales';

/**
 * Singleton state - se comparte entre todas las instancias de usePerfil()
 * Esto evita peticiones duplicadas cuando el componente se remounta
 */
const perfil = ref(null);
const loading = ref(true);
const error = ref(null);
const isEditing = ref(false);
const localData = ref({});
const saving = ref(false);
const errors = ref({});
let initialized = false;

export function usePerfil(options = {}) {
    const { autoFetch = true } = options;

    const configuracion = computed(() => store.getters.getConfig);
    const isLogged = computed(() => store.getters.isLogged);

    const extractPerfilPayload = response => response?.data?.datos ?? response?.data?.data ?? response?.data ?? response ?? {};
    const shouldFallbackToService = err => !err?.response;

    const dispatchLegacyPerfil = async(actionKey, payload = {}) => {
        const action = config.getVuexAlias(actionKey);
        return store.dispatch(action, payload);
    };

    const getPerfil = async() => {
        loading.value = true;
        error.value = null;
        try {
            const hashTeatro = configuracion.value?.hash_teatro;
            let datos;

            try {
                const response = await dispatchLegacyPerfil('front.perfil.get', {
                    hash: hashTeatro,
                    tid: hashTeatro,
                });
                datos = extractPerfilPayload(response);
            } catch (legacyError) {
                if (!shouldFallbackToService(legacyError)) {
                    throw legacyError;
                }
                datos = await datosPersonalesService.getDatosPersonales(hashTeatro);
            }

            perfil.value = datos;
            if (!isEditing.value) {
                localData.value = cloneDeep(datos || {});
            }
        } catch(err) {
            error.value = err?.message ?? t('perfil.error_cargar_datos');
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error al obtener perfil:', err);
            }
        } finally {
            loading.value = false;
        }
    };

    const startEditing = () => {
        localData.value = cloneDeep(perfil.value || {});
        errors.value = {};
        isEditing.value = true;
    };

    const cancelEditing = () => {
        localData.value = cloneDeep(perfil.value || {});
        errors.value = {};
        isEditing.value = false;
    };

    const save = async validateFn => {
        if (validateFn && !(await validateFn())) {
            return { success: false };
        }

        saving.value = true;
        errors.value = {};

        try {
            const hashTeatro = configuracion.value?.hash_teatro;
            const payload = {
                hash: hashTeatro,
                tid: hashTeatro,
                name: localData.value.name,
                surnames: localData.value.surnames,
                email: localData.value.email || perfil.value?.email || null,
                telefono: localData.value.telefono,
                nif: localData.value.nif,
                fecha_nacimiento: localData.value.fecha_nacimiento || null,
                pais_id: localData.value.pais?.id || localData.value.pais_id || null,
                provincia_id: localData.value.provincia?.id || localData.value.provincia_id || null,
                localidad_id: localData.value.localidad?.id || localData.value.localidad_id || null,
            };

            try {
                await dispatchLegacyPerfil('front.perfil.update', payload);
            } catch (legacyError) {
                if (!shouldFallbackToService(legacyError)) {
                    throw legacyError;
                }
                await datosPersonalesService.updateDatosPersonales(hashTeatro, payload);
            }

            await getPerfil();
            isEditing.value = false;

            return { success: true };
        } catch(err) {
            const backendErrors = err.response?.data?.errors;
            if (backendErrors) {
                errors.value = backendErrors;
            } else {
                errors.value = { general: t('perfil.error_guardar') };
            }
            return { success: false };
        } finally {
            saving.value = false;
        }
    };

    const checkAuth = () => {
        if (!isLogged.value) {
            router.push({ name: 'login' });
            return false;
        }
        return true;
    };

    onMounted(async() => {
        // Solo fetchear la primera vez o si no hay datos
        if (!autoFetch || initialized) {
            loading.value = false;
            return;
        }

        if (!checkAuth()) {
            loading.value = false;
            return;
        }

        initialized = true;
        await getPerfil();
    });

    const resetPerfil = () => {
        perfil.value = null;
        loading.value = true;
        error.value = null;
        isEditing.value = false;
        localData.value = {};
        saving.value = false;
        errors.value = {};
        initialized = false;
    };

    return {
        loading,
        error,
        perfil,
        isEditing,
        localData,
        saving,
        errors,
        resetPerfil,
        startEditing,
        cancelEditing,
        save,
        getPerfil,
    };
}
